import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Ruler, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import {
  fetchSubBrands, addSubBrand, deleteSubBrand,
  fetchUnits, addUnit, deleteUnit,
  type SetupItem,
} from './setupQueries';

function isDuplicate(value: string, items: SetupItem[]): boolean {
  return items.some((item) => item.name.toLowerCase() === value.toLowerCase().trim());
}

function SetupSection({
  icon: Icon,
  title,
  items,
  newValue,
  onNewValueChange,
  onAdd,
  onDelete,
  isPending,
  placeholder,
  examples,
}: {
  icon: React.ElementType;
  title: string;
  items: SetupItem[];
  newValue: string;
  onNewValueChange: (v: string) => void;
  onAdd: () => void;
  onDelete: (id: string, name: string) => void;
  isPending: boolean;
  placeholder: string;
  examples: string[];
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gold-600" />
        <h3 className="font-medium text-sm">{title}</h3>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder={placeholder}
          value={newValue}
          onChange={(e) => onNewValueChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && newValue.trim()) onAdd(); }}
          className="h-8 text-sm"
        />
        <Button size="sm" onClick={onAdd} disabled={!newValue.trim() || isPending} className="shrink-0">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {items.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">No {title.toLowerCase()} yet.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between rounded-md border bg-background px-3 py-2">
            <span className="text-sm">{item.name}</span>
            <Button
              variant="ghost" size="icon" className="h-6 w-6"
              onClick={() => { if (confirm(`Delete "${item.name}"?`)) onDelete(item.id, item.name); }}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {examples.map((ex) => (
          <button
            key={ex}
            onClick={() => onNewValueChange(ex)}
            className="text-xs rounded border px-2.5 py-1 text-muted-foreground hover:border-gold-400 hover:text-foreground transition-colors"
          >
            + {ex}
          </button>
        ))}
      </div>
    </div>
  );
}

export function InventorySetupDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [newSubBrand, setNewSubBrand] = useState('');
  const [newUnit, setNewUnit] = useState('');

  const { data: subBrands = [] } = useQuery({ queryKey: ['inventory-sub-brands'], queryFn: fetchSubBrands, enabled: open });
  const { data: units = [] } = useQuery({ queryKey: ['inventory-units'], queryFn: fetchUnits, enabled: open });

  const addSBr = useMutation({
    mutationFn: addSubBrand,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory-sub-brands'] }); setNewSubBrand(''); toast({ title: 'Sub-brand added', variant: 'success' }); },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });
  const delSBr = useMutation({
    mutationFn: deleteSubBrand,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-sub-brands'] }),
  });

  const addUn = useMutation({
    mutationFn: addUnit,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory-units'] }); setNewUnit(''); toast({ title: 'Unit added', variant: 'success' }); },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });
  const delUn = useMutation({
    mutationFn: deleteUnit,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-units'] }),
  });

  const handleAdd = useCallback((value: string, items: SetupItem[], label: string, mutate: (v: string) => void) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (isDuplicate(trimmed, items)) {
      toast({ title: `"${trimmed}" already exists`, description: `This ${label} is already in your list.`, variant: 'default' });
      return;
    }
    mutate(trimmed);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Inventory setup</DialogTitle>
          <DialogDescription>
            Manage sub-brands and units. Brands are added inside each category from the Categories tab.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2">
          <SetupSection
            icon={Layers}
            title="Sub Brands"
            items={subBrands}
            newValue={newSubBrand}
            onNewValueChange={setNewSubBrand}
            onAdd={() => handleAdd(newSubBrand, subBrands, 'sub-brand', addSBr.mutate)}
            onDelete={(id) => delSBr.mutate(id)}
            isPending={addSBr.isPending}
            placeholder="e.g. Premium"
            examples={['Premium', 'Standard', 'Economy', 'Pro', 'Lite']}
          />

          <SetupSection
            icon={Ruler}
            title="Units"
            items={units}
            newValue={newUnit}
            onNewValueChange={setNewUnit}
            onAdd={() => handleAdd(newUnit, units, 'unit', addUn.mutate)}
            onDelete={(id) => delUn.mutate(id)}
            isPending={addUn.isPending}
            placeholder="e.g. kg"
            examples={['piece', 'sheet', 'kg', 'box', 'roll', 'litre', 'metre']}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
